import { useState } from "react";
import {
  dangerBtnClass,
  ghostBtnClass,
  inputClass,
  primaryBtnClass,
} from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";
import { openOpsPaperworkDeal } from "@/lib/opsLookup/openOpsPaperworkDeal";

export function OrderIntakeReviewActions(props: {
  row: OpsOrderIntakeRow;
  variant: "ops_reviewer" | "mfg_accepter";
  actionBusy: boolean;
  paperworkReady: boolean;
  onAccept: (plantOverride: string | null) => Promise<boolean>;
  onHold: (reason: string) => Promise<boolean>;
  onCancel: () => Promise<boolean>;
  onReturnToHold: (reason: string) => Promise<boolean>;
}) {
  const { row, variant, actionBusy, paperworkReady, onAccept, onHold, onCancel, onReturnToHold } =
    props;
  const [plantOverride, setPlantOverride] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const isOps = variant === "ops_reviewer";
  const canAct = row.status === "pending_review" || row.status === "on_hold";
  const canReturnToHold = row.status === "accepted";

  if (canAct) {
    return (
      <div className="mt-4 space-y-3">
        {!isOps ? (
          <div className="space-y-1">
            <label className="block text-xs text-gray-600 dark:text-zinc-300">
              Plant override (optional)
              <input
                className={`${inputClass} mt-1`}
                value={plantOverride}
                onChange={(e) => setPlantOverride(e.target.value)}
                placeholder={row.proposed_plant_id ?? "uuid of plant to use instead"}
                disabled={actionBusy}
              />
            </label>
          </div>
        ) : null}

        <button
          type="button"
          className={primaryBtnClass}
          disabled={actionBusy || !paperworkReady}
          title={paperworkReady ? undefined : "Resolve failed paperwork checks before accepting"}
          onClick={() => {
            void onAccept(isOps ? null : plantOverride.trim() === "" ? null : plantOverride.trim());
          }}
        >
          {actionBusy ? "Working…" : isOps ? "Accept — paperwork OK" : "Accept order"}
        </button>

        <div className="space-y-1">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Hold reason
            <input
              className={`${inputClass} mt-1`}
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="Ask the submitter to clarify the spec or fix data."
              disabled={actionBusy}
            />
          </label>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={actionBusy || holdReason.trim() === ""}
            onClick={() => {
              void onHold(holdReason);
            }}
          >
            {actionBusy ? "Working…" : "Place on hold"}
          </button>
        </div>

        <button
          type="button"
          className={dangerBtnClass}
          disabled={actionBusy}
          onClick={() => {
            void onCancel();
          }}
        >
          {actionBusy ? "Working…" : "Cancel order"}
        </button>
      </div>
    );
  }

  if (canReturnToHold) {
    return (
      <div className="mt-4 space-y-2">
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-300">
          {isOps
            ? "Accepted. Continue this customer in Deals. Return to hold sends the intake back and archives that deal; the unit and serial stay for a later accept."
            : `Already accepted. Materialized deal: ${row.materialized_deal_id ?? "—"}. Use Return-to-Hold to flip the intake back to on_hold and archive the deal; the unit and serial are preserved for a future re-Accept.`}
        </div>
        {isOps && row.materialized_deal_id ? (
          <button
            type="button"
            className={primaryBtnClass}
            onClick={() => openOpsPaperworkDeal(row.materialized_deal_id as string)}
          >
            Continue in Deals
          </button>
        ) : null}
        <div className="space-y-1">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            Return-to-Hold reason
            <input
              className={`${inputClass} mt-1`}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="What needs to change before this can be re-accepted?"
              disabled={actionBusy}
            />
          </label>
          <button
            type="button"
            className={ghostBtnClass}
            disabled={actionBusy || returnReason.trim() === ""}
            onClick={() => {
              void onReturnToHold(returnReason);
            }}
          >
            {actionBusy ? "Working…" : "Return to hold"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-300">
      This intake has been cancelled and is awaiting 30-day archive cleanup.
    </div>
  );
}
